'use client';

import type { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';

import useConfirmation from './useConfirmationContext';

export type PendingNavigation = {
  type: 'anchor' | 'popstate' | 'router' | 'custom';
  url?: string;
  options?: NavigateOptions;
  navigateCallback?: () => void | Promise<void>;
  customMessage?: string;
  resolvePromise?: (value: boolean) => void;
  event?: Event;
};

/**
 * Hook to handle warning users about unsaved changes during navigation
 * @param unsaved Boolean indicating if there are unsaved changes
 * @returns Object containing utilities for handling navigation with unsaved changes
 */
export function useWarnIfUnsavedChanges(unsaved: boolean) {
  const router = useRouter();
  const translate = useTranslations();
  const { confirm } = useConfirmation();
  const intentionalNavigation = useRef(false); // Flag for intentional navigation
  const bypassWarnings = useRef(false); // Flag to bypass all warnings temporarily

  // Maintain modalState for backward compatibility
  const [modalState, setModalState] = useState(() => ({
    isOpen: false,
    title: translate('navigationModal.title'),
    description: translate('navigationModal.unsavedChangesMessage'),
    confirmText: translate('modal.confirm'),
    rejectText: translate('modal.cancel'),
  }));

  /**
   * Get default confirmation options
   * @returns Default confirmation dialog options
   */
  const getConfirmOptions = useCallback(() => ({
    title: translate('navigationModal.title'),
    description: translate('navigationModal.unsavedChangesMessage'),
    confirmText: translate('modal.confirm'),
    rejectText: translate('modal.cancel'),
  }), [translate]);

  /**
   * Handle anchor link clicks with confirmation for unsaved changes
   * @param e Mouse event from anchor click
   */
  const handleAnchorClick = useCallback(async (e: MouseEvent) => {
    if (e.button !== 0) {
      return; // only handle left-clicks
    }

    if (intentionalNavigation.current === true || bypassWarnings.current === true) {
      return;
    }

    const targetUrl = (e.currentTarget as HTMLAnchorElement).href;
    const currentUrl = window.location.href;

    if (targetUrl !== currentUrl && unsaved === true) {
      e.preventDefault();

      // Use the confirmation context
      const confirmed = await confirm(getConfirmOptions());

      if (confirmed === true) {
        intentionalNavigation.current = true;
        window.location.href = targetUrl;
      }
    }
  }, [unsaved, confirm, getConfirmOptions]);

  /**
   * Add click event listeners to all anchor elements
   */
  const addAnchorListeners = useCallback(() => {
    const anchorElements = document.querySelectorAll<HTMLAnchorElement>('a[href]');
    anchorElements.forEach((anchor) => {
      anchor.removeEventListener('click', handleAnchorClick); // Ensure no duplicate event listeners
      anchor.addEventListener('click', handleAnchorClick);
    });
  }, [handleAnchorClick]);

  // Setup anchor click listeners and mutation observer
  useEffect(() => {
    const mutationObserver = new MutationObserver(addAnchorListeners);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    addAnchorListeners();

    // Capture for cleanup
    const cleanupHandler = handleAnchorClick;

    return () => {
      mutationObserver.disconnect();
      const anchorElements = document.querySelectorAll<HTMLAnchorElement>('a[href]');
      anchorElements.forEach((anchor) => {
        anchor.removeEventListener('click', cleanupHandler);
      });
    };
  }, [addAnchorListeners, handleAnchorClick]);

  // Handle browser's beforeunload and popstate events
  useEffect(() => {
    /**
     * Handler for beforeunload event to prevent accidental window/tab close
     */
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      if (intentionalNavigation.current === false && bypassWarnings.current === false && unsaved === true) {
        // Browser close/refresh still needs to use standard dialog
        e.preventDefault();
        e.returnValue = ''; // required for Chrome to show the confirmation dialog
      }
    };

    /**
     * Handler for popstate event (browser back/forward navigation)
     */
    const handlePopState = async (e: PopStateEvent) => {
      if (intentionalNavigation.current === false && bypassWarnings.current === false && unsaved === true) {
        e.preventDefault();

        // Use the confirmation context
        const confirmed = await confirm(getConfirmOptions());

        if (confirmed === true) {
          intentionalNavigation.current = true;
          // Allow the navigation by not preventing default
        } else {
          // Prevent back navigation by pushing current URL to history again
          window.history.pushState(null, '', window.location.href);
        }
      }
    };

    if (unsaved === true) {
      window.addEventListener('beforeunload', beforeUnloadHandler);
      window.addEventListener('popstate', handlePopState);
    } else {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      window.removeEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [unsaved, confirm, getConfirmOptions]);

  // Override router.push to include confirmation
  useEffect(() => {
    const originalPush = router.push.bind(router);

    router.push = async (url: string, options?: NavigateOptions) => {
      if (intentionalNavigation.current === true || bypassWarnings.current === true || unsaved === false) {
        originalPush(url, options);
      } else {
        // Use the confirmation context
        const confirmed = await confirm(getConfirmOptions());

        if (confirmed === true) {
          intentionalNavigation.current = true;
          originalPush(url, options);
        }
      }
    };

    return () => {
      router.push = originalPush;
    };
  }, [router, unsaved, confirm, getConfirmOptions]);

  /**
   * Utility to handle confirmation for actions that may discard changes
   * @param actionCallback Function to execute if confirmed
   * @param customMessage Optional custom confirmation message
   * @returns Promise that resolves to true if confirmed, false otherwise
   */
  const confirmAction = useCallback(async (
    actionCallback: () => void | Promise<void>,
    customMessage?: string,
  ): Promise<boolean> => {
    if (unsaved === false || bypassWarnings.current === true) {
      // No unsaved changes or warnings bypassed, proceed with the action
      await actionCallback();
      return true;
    }

    // Use the confirmation context
    const options = getConfirmOptions();
    if (customMessage !== undefined) {
      options.description = customMessage;
    }

    const confirmed = await confirm(options);

    if (confirmed === true) {
      await actionCallback();
      return true;
    }

    return false;
  }, [unsaved, confirm, getConfirmOptions]);

  /**
   * Temporarily disables all unsaved changes warnings
   */
  const disableWarnings = useCallback(() => {
    bypassWarnings.current = true;
  }, []);

  /**
   * Re-enables unsaved changes warnings
   */
  const enableWarnings = useCallback(() => {
    bypassWarnings.current = false;
  }, []);

  /**
   * Executes an action with warnings temporarily disabled
   * @param action Function to execute without triggering unsaved changes warnings
   * @returns Result of the action function
   */
  const executeWithoutWarnings = useCallback(async <T>(action: () => Promise<T> | T): Promise<T> => {
    disableWarnings();
    try {
      const result = await action();
      return result;
    } finally {
      enableWarnings();
    }
  }, [disableWarnings, enableWarnings]);

  // Modal-related functions for backward compatibility
  const onOpenChange = useCallback((open: boolean) => {
    setModalState(prev => ({ ...prev, isOpen: open }));
  }, []);

  const onConfirm = useCallback(() => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const onReject = useCallback(() => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  }, []);

  return {
    setIntentionalNavigation: (value: boolean) => {
      intentionalNavigation.current = value;
    },
    confirmAction,
    disableWarnings,
    enableWarnings,
    executeWithoutWarnings,
    // Keep the modal property for backward compatibility
    modal: {
      isOpen: modalState.isOpen,
      onOpenChange,
      title: modalState.title,
      description: modalState.description,
      onConfirm,
      onReject,
      confirmText: modalState.confirmText,
      rejectText: modalState.rejectText,
    },
  };
}

export default useWarnIfUnsavedChanges;
